import { useEffect, useState, useRef } from 'react';

const defaultFigures = [
  { value: '2017', label: 'Création', description: 'Association créée fin 2017' },
  { value: '50+', label: 'Membres', description: 'Membres fondateurs et actifs' },
  { value: '8', label: 'Départements', description: 'Bourgogne-Franche-Comté' },
  { value: '1er', label: 'Réseau', description: 'Réseau régional finance & entreprise' }
];

const FiguresSection = ({ content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const figures = content?.items || defaultFigures;
  const extra = content?.extra_fields || {};

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="figures"
      ref={sectionRef}
      data-testid="figures-section"
      className="py-24 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a8a]/10 text-[#1e3a8a] text-sm font-inter font-medium tracking-wide uppercase rounded-full mb-4">
            {extra.badge_text || 'En quelques chiffres'}
          </span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content?.title || 'Chiffres clés'}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#1e3a8a] to-[#D4AF37] mx-auto mb-6" />
          <p className="text-lg text-slate-600 font-inter">
            {content?.subtitle || 'La Place Financière en quelques repères'}
          </p>
        </div>

        {/* Figures Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {figures.map((figure, idx) => (
            <div
              key={idx}
              className={`bg-white p-8 text-center shadow-sm hover:shadow-xl transition-all duration-500 group ${
                isVisible ? 'animate-count' : 'opacity-0'
              }`}
              style={{ animationDelay: `${idx * 150}ms` }}
              data-testid={`figure-card-${idx}`}
            >
              {/* Value */}
              <div className="relative mb-4">
                <span className="font-playfair text-5xl lg:text-6xl font-bold text-[#1e3a8a] group-hover:text-[#D4AF37] transition-colors duration-300">
                  {figure.value}
                </span>
                {/* Decorative line */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              {/* Label */}
              <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-2">
                {figure.label}
              </h3>
              
              {/* Description */}
              <p className="text-sm text-slate-500 font-inter">
                {figure.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FiguresSection;
